import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Shell } from "./components/layout/Shell";
import { TenantProvider, useTenant } from "./lib/TenantContext";
import { ToastProvider, ToastContainer } from "./lib/toast";
import { TenantServiceWorkerBridge } from "./components/TenantServiceWorkerBridge";
import { TenantRevisionBoundary } from "./components/TenantRevisionBoundary";

// Lazy loading de páginas para mejor performance
const VisualTouch = lazy(() => import("./pages/pos/VisualTouch"));
const LinearDesktop = lazy(() => import("./pages/pos/LinearDesktop"));
const BankReconciliation = lazy(() => import("./pages/ai/BankReconciliation"));
const Taxes = lazy(() => import("./pages/hub/Taxes"));
const Settings = lazy(() => import("./pages/hub/Settings"));
const Dashboard = lazy(() => import("./pages/hub/Dashboard"));
const InventoryHub = lazy(() => import("./pages/inventory/InventoryHub"));
const CustomersPage = lazy(() => import("./pages/hub/Customers"));
const FinancialReports = lazy(() => import("./pages/hub/FinancialReports"));
const TransactionsPage = lazy(() => import("./pages/hub/Transactions"));
const SuppliersPage = lazy(() => import("./pages/hub/Suppliers"));
const ProductsServicesPage = lazy(() => import("./pages/hub/ProductsServices"));
const ExpensesPage = lazy(() => import("./pages/hub/Expenses"));
const ServiceOrdersPage = lazy(() => import("./pages/hub/ServiceOrders"));
const WorkshopPage = lazy(() => import("./pages/hub/Workshop"));
const HotelPage = lazy(() => import("./pages/hub/Hotel"));
const ClinicPage = lazy(() => import("./pages/hub/Clinic"));
const GymPage = lazy(() => import("./pages/hub/Gym"));
const ConstructionPage = lazy(() => import("./pages/hub/Construction"));
const EducationPage = lazy(() => import("./pages/hub/Education"));
const RealEstatePage = lazy(() => import("./pages/hub/RealEstate"));
const VeterinaryPage = lazy(() => import("./pages/hub/Veterinary"));
const DentalPage = lazy(() => import("./pages/hub/Dental"));
const FloristPage = lazy(() => import("./pages/hub/Florist"));
const CarDealerPage = lazy(() => import("./pages/hub/CarDealer"));
const FuneralPage = lazy(() => import("./pages/hub/Funeral"));
const PhotoStudioPage = lazy(() => import("./pages/hub/PhotoStudio"));
const InventoryTrackingPage = lazy(
  () => import("./pages/hub/InventoryTracking"),
);
const ECommerceManagerPage = lazy(() => import("./pages/hub/ECommerceManager"));
const PaymentGatewayPage = lazy(() => import("./pages/hub/PaymentGateway"));
const OdontogramPage = lazy(() => import("./pages/hub/Odontogram"));
const PhotoGalleryPage = lazy(() => import("./pages/hub/PhotoGallery"));
const TestDrivePage = lazy(() => import("./pages/hub/TestDrive"));
const PromotionsPage = lazy(() => import("./pages/hub/Promotions"));
const HRPayroll = lazy(() => import("./pages/hub/HRPayroll"));
const CashShifts = lazy(() => import("./pages/hub/CashShifts"));
const Delivery = lazy(() => import("./pages/hub/Delivery"));
const Accounting = lazy(() => import("./pages/hub/Accounting"));
const CRMPage = lazy(() => import("./pages/hub/CRM"));
const Reservations = lazy(() => import("./pages/hub/Reservations"));
const KDS = lazy(() => import("./pages/hub/KDS"));
const WarrantiesPage = lazy(() => import("./pages/hub/Warranties"));
const InstallationsPage = lazy(() => import("./pages/hub/Installations"));
const PurchaseOrders = lazy(() => import("./pages/hub/PurchaseOrders"));
const CreditAccounts = lazy(() => import("./pages/hub/CreditAccounts"));
const BranchesPage = lazy(() => import("./pages/hub/Branches"));
const PrintInvoice = lazy(() => import("./pages/hub/PrintInvoice"));
const ServiceContracts = lazy(() => import("./pages/hub/ServiceContracts"));
const BIAnalytics = lazy(() => import("./pages/hub/BIAnalytics"));
const AuditLog = lazy(() => import("./pages/hub/AuditLog"));
const MultiCurrency = lazy(() => import("./pages/hub/MultiCurrency"));
const Commissions = lazy(() => import("./pages/hub/Commissions"));
const AdvancedPayroll = lazy(() => import("./pages/hub/AdvancedPayroll"));
const BankingIntegration = lazy(() => import("./pages/hub/BankingIntegration"));
const ECommerce = lazy(() => import("./pages/hub/ECommerce"));
const WebchatOrdering = lazy(() => import("./pages/hub/WebchatOrdering"));
const AccountsPayable = lazy(() => import("./pages/hub/AccountsPayable"));
const ReceivePayment = lazy(() => import("./pages/hub/ReceivePayment"));
const Storefront = lazy(() => import("./pages/store/Storefront"));
const MeatShop = lazy(() => import("./pages/hub/MeatShop"));

// Nuevas páginas - Contabilidad avanzada
const GeneralLedger = lazy(() => import("./pages/hub/GeneralLedger"));
const FixedAssets = lazy(() => import("./pages/hub/FixedAssets"));
const PeriodClosing = lazy(() => import("./pages/hub/PeriodClosing"));

// Nuevas páginas - Portales y Workflows
const EmployeePortal = lazy(() => import("./pages/hub/EmployeePortal"));
const CustomerPortal = lazy(() => import("./pages/hub/CustomerPortal"));
const PurchaseRequisitions = lazy(
  () => import("./pages/hub/PurchaseRequisitions"),
);
const InventoryAdvanced = lazy(() => import("./pages/hub/InventoryAdvanced"));

import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
const DGIIConfig = lazy(() => import("./pages/hub/DGIIConfig"));

// OsirisBrain Admin pages
import AdminShell from "./components/layout/AdminShell";
import AdminDashboard from "./pages/admin/AdminDashboard";
import TenantsList from "./pages/admin/TenantsList";
import TenantDetail from "./pages/admin/TenantDetail";
import AdminLogin from "./pages/admin/AdminLogin";

// Loading component
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin w-10 h-10 rounded-full border-4 border-brand-200 border-t-brand-500"></div>
      <p className="text-sm text-gray-500">Cargando...</p>
    </div>
  </div>
);

// Protected Route Wrapper — Verifica autenticación
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50">
        <div className="animate-spin w-8 h-8 rounded-full border-4 border-brand-200 border-t-brand-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Authorized Route Wrapper — Verifica autenticación Y permisos por ruta
const AuthorizedRoute = ({
  route,
  children,
}: {
  route: string;
  children: React.ReactNode;
}) => {
  const { user, loading, canAccess, userRole } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50">
        <div className="animate-spin w-8 h-8 rounded-full border-4 border-brand-200 border-t-brand-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redireccionar al vendedor (sales) de forma limpia y transparente al POS Lineal si intenta entrar al dashboard
  if (route === "/" && userRole === "sales") {
    return <Navigate to="/pos/linear" replace />;
  }

  if (!canAccess(route)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Acceso Denegado
          </h2>
          <p className="text-gray-500 mb-4">
            No tienes permisos para acceder a esta sección.
          </p>
          <a href="/" className="text-brand-500 font-semibold hover:underline">
            Volver al Panel
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { isSuperAdmin, loading } = useTenant();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-50">
        <div className="animate-spin w-8 h-8 rounded-full border-4 border-brand-200 border-t-brand-500"></div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* OsirisBrain Super Admin Silo */}
        {isSuperAdmin && (
          <>
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="tenants" element={<TenantsList />} />
              <Route path="tenants/:id" element={<TenantDetail />} />
            </Route>
          </>
        )}

        {/* Tenant (Client) Silo */}
        {!isSuperAdmin && (
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Shell />
              </ProtectedRoute>
            }
          >
            {/* Dashboard */}
            <Route
              index
              element={
                <AuthorizedRoute route="/">
                  <Dashboard />
                </AuthorizedRoute>
              }
            />

            {/* Ventas y POS */}
            <Route
              path="sales/transactions"
              element={
                <AuthorizedRoute route="/sales/transactions">
                  <TransactionsPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="pos/linear"
              element={
                <AuthorizedRoute route="/pos/linear">
                  <LinearDesktop />
                </AuthorizedRoute>
              }
            />
            <Route
              path="pos/visual"
              element={
                <AuthorizedRoute route="/pos/visual">
                  <VisualTouch />
                </AuthorizedRoute>
              }
            />
            <Route
              path="sales/products"
              element={
                <AuthorizedRoute route="/sales/products">
                  <ProductsServicesPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/promotions"
              element={
                <AuthorizedRoute route="/hub/promotions">
                  <PromotionsPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/print-invoice"
              element={
                <AuthorizedRoute route="/hub/print-invoice">
                  <PrintInvoice />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/receive-payment"
              element={
                <AuthorizedRoute route="/hub/receive-payment">
                  <ReceivePayment />
                </AuthorizedRoute>
              }
            />

            {/* Clientes y CRM */}
            <Route
              path="hub/customers"
              element={
                <AuthorizedRoute route="/hub/customers">
                  <CustomersPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/crm"
              element={
                <AuthorizedRoute route="/hub/crm">
                  <CRMPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/credit-accounts"
              element={
                <AuthorizedRoute route="/hub/credit-accounts">
                  <CreditAccounts />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/service-orders"
              element={
                <AuthorizedRoute route="/hub/service-orders">
                  <ServiceOrdersPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/workshop"
              element={
                <AuthorizedRoute route="/hub/workshop">
                  <WorkshopPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/hotel"
              element={
                <AuthorizedRoute route="/hub/hotel">
                  <HotelPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/clinic"
              element={
                <AuthorizedRoute route="/hub/clinic">
                  <ClinicPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/gym"
              element={
                <AuthorizedRoute route="/hub/gym">
                  <GymPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/construction"
              element={
                <AuthorizedRoute route="/hub/construction">
                  <ConstructionPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/education"
              element={
                <AuthorizedRoute route="/hub/education">
                  <EducationPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/realestate"
              element={
                <AuthorizedRoute route="/hub/realestate">
                  <RealEstatePage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/veterinary"
              element={
                <AuthorizedRoute route="/hub/veterinary">
                  <VeterinaryPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/dental"
              element={
                <AuthorizedRoute route="/hub/dental">
                  <DentalPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/florist"
              element={
                <AuthorizedRoute route="/hub/florist">
                  <FloristPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/car-dealer"
              element={
                <AuthorizedRoute route="/hub/car-dealer">
                  <CarDealerPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/funeral"
              element={
                <AuthorizedRoute route="/hub/funeral">
                  <FuneralPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/meat-shop"
              element={
                <AuthorizedRoute route="/hub/meat-shop">
                  <MeatShop />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/photo-studio"
              element={
                <AuthorizedRoute route="/hub/photo-studio">
                  <PhotoStudioPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/inventory-tracking"
              element={
                <AuthorizedRoute route="/hub/inventory-tracking">
                  <InventoryTrackingPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/ecommerce-manager"
              element={
                <AuthorizedRoute route="/hub/ecommerce-manager">
                  <ECommerceManagerPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/payment-gateway"
              element={
                <AuthorizedRoute route="/hub/payment-gateway">
                  <PaymentGatewayPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/odontogram"
              element={
                <AuthorizedRoute route="/hub/odontogram">
                  <OdontogramPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/photo-gallery"
              element={
                <AuthorizedRoute route="/hub/photo-gallery">
                  <PhotoGalleryPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/test-drive"
              element={
                <AuthorizedRoute route="/hub/test-drive">
                  <TestDrivePage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="customer-portal"
              element={
                <AuthorizedRoute route="/customer-portal">
                  <CustomerPortal />
                </AuthorizedRoute>
              }
            />

            {/* Inventario */}
            <Route
              path="inventory/overview"
              element={
                <AuthorizedRoute route="/inventory/overview">
                  <InventoryHub />
                </AuthorizedRoute>
              }
            />
            <Route
              path="inventory/advanced"
              element={
                <AuthorizedRoute route="/inventory/advanced">
                  <InventoryAdvanced />
                </AuthorizedRoute>
              }
            />

            {/* Gastos y Compras */}
            <Route
              path="hub/expenses"
              element={
                <AuthorizedRoute route="/hub/expenses">
                  <ExpensesPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/suppliers"
              element={
                <AuthorizedRoute route="/hub/suppliers">
                  <SuppliersPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/purchase-orders"
              element={
                <AuthorizedRoute route="/hub/purchase-orders">
                  <PurchaseOrders />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/purchase-requisitions"
              element={
                <AuthorizedRoute route="/hub/purchase-requisitions">
                  <PurchaseRequisitions />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/accounts-payable"
              element={
                <AuthorizedRoute route="/hub/accounts-payable">
                  <AccountsPayable />
                </AuthorizedRoute>
              }
            />

            {/* Contabilidad - Módulos nuevos */}
            <Route
              path="hub/accounting"
              element={
                <AuthorizedRoute route="/hub/accounting">
                  <Accounting />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/ledger"
              element={
                <AuthorizedRoute route="/hub/ledger">
                  <GeneralLedger />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/fixed-assets"
              element={
                <AuthorizedRoute route="/hub/fixed-assets">
                  <FixedAssets />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/period-closing"
              element={
                <AuthorizedRoute route="/hub/period-closing">
                  <PeriodClosing />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/reports"
              element={
                <AuthorizedRoute route="/hub/reports">
                  <FinancialReports />
                </AuthorizedRoute>
              }
            />
            <Route
              path="ai/reconciliation"
              element={
                <AuthorizedRoute route="/ai/reconciliation">
                  <BankReconciliation />
                </AuthorizedRoute>
              }
            />

            {/* Impuestos DGII */}
            <Route
              path="hub/taxes"
              element={
                <AuthorizedRoute route="/hub/taxes">
                  <Taxes />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/dgii-config"
              element={
                <AuthorizedRoute route="/hub/dgii-config">
                  <DGIIConfig />
                </AuthorizedRoute>
              }
            />

            {/* Recursos Humanos */}
            <Route
              path="hub/hr"
              element={
                <AuthorizedRoute route="/hub/hr">
                  <HRPayroll />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/advanced-payroll"
              element={
                <AuthorizedRoute route="/hub/advanced-payroll">
                  <AdvancedPayroll />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/commissions"
              element={
                <AuthorizedRoute route="/hub/commissions">
                  <Commissions />
                </AuthorizedRoute>
              }
            />
            <Route
              path="employee-portal"
              element={
                <AuthorizedRoute route="/employee-portal">
                  <EmployeePortal />
                </AuthorizedRoute>
              }
            />

            {/* Operaciones */}
            <Route
              path="hub/cash-shifts"
              element={
                <AuthorizedRoute route="/hub/cash-shifts">
                  <CashShifts />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/delivery"
              element={
                <AuthorizedRoute route="/hub/delivery">
                  <Delivery />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/kds"
              element={
                <AuthorizedRoute route="/hub/kds">
                  <KDS />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/reservations"
              element={
                <AuthorizedRoute route="/hub/reservations">
                  <Reservations />
                </AuthorizedRoute>
              }
            />

            {/* Post-Venta */}
            <Route
              path="hub/warranties"
              element={
                <AuthorizedRoute route="/hub/warranties">
                  <WarrantiesPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/installations"
              element={
                <AuthorizedRoute route="/hub/installations">
                  <InstallationsPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/service-contracts"
              element={
                <AuthorizedRoute route="/hub/service-contracts">
                  <ServiceContracts />
                </AuthorizedRoute>
              }
            />

            {/* Multi-Ubicación */}
            <Route
              path="hub/branches"
              element={
                <AuthorizedRoute route="/hub/branches">
                  <BranchesPage />
                </AuthorizedRoute>
              }
            />

            {/* Inteligencia y Finanzas */}
            <Route
              path="hub/bi-analytics"
              element={
                <AuthorizedRoute route="/hub/bi-analytics">
                  <BIAnalytics />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/multi-currency"
              element={
                <AuthorizedRoute route="/hub/multi-currency">
                  <MultiCurrency />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/banking"
              element={
                <AuthorizedRoute route="/hub/banking">
                  <BankingIntegration />
                </AuthorizedRoute>
              }
            />

            {/* E-Commerce */}
            <Route
              path="hub/ecommerce"
              element={
                <AuthorizedRoute route="/hub/ecommerce">
                  <ECommerce />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/webchat"
              element={
                <AuthorizedRoute route="/hub/webchat">
                  <WebchatOrdering />
                </AuthorizedRoute>
              }
            />

            {/* Seguridad y Config */}
            <Route
              path="hub/audit-log"
              element={
                <AuthorizedRoute route="/hub/audit-log">
                  <AuditLog />
                </AuthorizedRoute>
              }
            />
            <Route
              path="hub/settings"
              element={
                <AuthorizedRoute route="/hub/settings">
                  <Settings />
                </AuthorizedRoute>
              }
            />
          </Route>
        )}

        {/* Public Storefront */}
        <Route path="/store/:storeSlug" element={<Storefront />} />

        {/* Catch-all redirect to ensure isolated silos */}
        <Route
          path="*"
          element={<Navigate to={isSuperAdmin ? "/admin" : "/"} replace />}
        />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <TenantProvider>
      <TenantScopedRuntime />
    </TenantProvider>
  );
}

const TenantScopedRuntime = () => {
  const { tenantRevision } = useTenant();

  return (
    <TenantRevisionBoundary revision={tenantRevision}>
      <TenantServiceWorkerBridge />
      <ToastProvider>
        <BrowserRouter>
          <AppRoutes />
          <ToastContainer />
        </BrowserRouter>
      </ToastProvider>
    </TenantRevisionBoundary>
  );
};

export default App;
